import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { PencilRuler, Plus, Send, Upload, X } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/common";
import Modal, { TextField, TextArea, SelectField } from "@/components/Modal";
import { toast } from "sonner";

export const PROJECT_STATUS = {
  soumis: { label: "Soumis", color: "bg-blue-100 text-blue-700" },
  etude: { label: "À l'étude", color: "bg-amber-100 text-amber-700" },
  echange: { label: "Échanges en cours", color: "bg-indigo-100 text-indigo-700" },
  valide: { label: "Validé", color: "bg-emerald-100 text-emerald-700" },
  commande: { label: "Commande", color: "bg-teal-100 text-teal-700" },
  livraison: { label: "Livraison", color: "bg-cyan-100 text-cyan-700" },
  installation: { label: "Installation", color: "bg-lime-100 text-lime-700" },
  cloture: { label: "Clôturé", color: "bg-green-100 text-green-700" },
  refuse: { label: "Refusé", color: "bg-red-100 text-red-600" },
};

export const PROJECT_TYPES = [
  { value: "construction", label: "Construction neuve" },
  { value: "renovation", label: "Rénovation" },
  { value: "electricite", label: "Électricité" },
  { value: "plomberie", label: "Plomberie" },
  { value: "climatisation", label: "Climatisation" },
  { value: "solaire", label: "Installation solaire" },
  { value: "carrelage", label: "Carrelage / Revêtement" },
  { value: "autre", label: "Autre" },
];

const STEPS = ["soumis", "etude", "echange", "valide", "commande", "livraison", "installation"];

export default function ProjectPage() {
  const { user } = useAuth();
  const [projects, setProjects] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [active, setActive] = useState(null);
  const load = useCallback(() => api.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([])), []);
  useEffect(() => { if (user) load(); }, [user, load]);

  if (!user) {
    return (
      <div className="max-w-xl mx-auto text-center py-12" data-testid="project-guest">
        <PencilRuler className="w-10 h-10 mx-auto text-[#C85A32] mb-3" />
        <h1 className="font-display text-2xl font-bold">Votre projet</h1>
        <p className="text-slate-500 mt-2">Décrivez votre projet de construction ou d'installation et notre équipe vous accompagne de l'étude jusqu'à la pose.</p>
        <Link to="/login" className="inline-block mt-4 bg-[#C85A32] text-white font-bold px-5 py-2.5 rounded-lg" data-testid="project-login-cta">Se connecter</Link>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto" data-testid="project-page">
      <div className="flex items-center justify-between mb-2">
        <h1 className="font-display text-2xl font-bold flex items-center gap-2"><PencilRuler className="w-6 h-6 text-[#C85A32]" /> Votre projet</h1>
        <button onClick={() => setShowNew(true)} className="bg-[#C85A32] text-white font-bold text-sm px-4 py-2 rounded-lg inline-flex items-center gap-1" data-testid="new-project-btn"><Plus className="w-4 h-4" /> Nouveau projet</button>
      </div>
      <p className="text-sm text-slate-500 mb-4">Soumettez votre besoin. Notre équipe l'étudie, échange avec vous, valide, puis pilote commande, livraison et installation.</p>
      {!projects ? <Loader /> : projects.length === 0 ? (
        <div className="text-center py-10 bg-white border border-dashed border-slate-200 rounded-xl">
          <p className="text-slate-400 text-sm">Aucun projet pour le moment.</p>
          <button onClick={() => setShowNew(true)} className="mt-3 text-[#C85A32] font-semibold text-sm" data-testid="empty-new-project">Décrire mon projet →</button>
        </div>
      ) : (
        <div className="space-y-2">
          {projects.map((p) => (
            <button key={p.id} onClick={() => setActive(p.id)} className="w-full text-left bg-white border border-slate-200 rounded-xl p-4 hover:border-[#C85A32] transition" data-testid={`project-${p.id}`}>
              <div className="flex items-center gap-2">
                <span className="font-num text-xs text-slate-400">{p.project_number}</span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${PROJECT_STATUS[p.status]?.color}`}>{PROJECT_STATUS[p.status]?.label}</span>
                <span className="ml-auto text-xs text-slate-400">{formatDate(p.updated_at)}</span>
              </div>
              <p className="font-semibold mt-1">{p.title}</p>
              <p className="text-sm text-slate-500 line-clamp-1">{p.description}</p>
            </button>
          ))}
        </div>
      )}
      {showNew && <NewProject onClose={() => setShowNew(false)} onDone={load} />}
      {active && <ProjectThread id={active} onClose={() => setActive(null)} onChanged={load} />}
    </div>
  );
}

function NewProject({ onClose, onDone }) {
  const [f, setF] = useState({ title: "", description: "", project_type: "construction", budget_estimate: "", zone: "", address: "", deadline: "" });
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setMedia((m) => [...m, data.url]);
      }
    } catch (err) { toast.error(apiError(err)); }
    setUploading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/projects", { ...f, media }); toast.success("Projet soumis"); onDone(); onClose(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Modal title="Nouveau projet" onClose={onClose} testid="project-form" size="lg">
      <form onSubmit={submit} className="space-y-3">
        <TextField label="Titre du projet" value={f.title} onChange={set("title")} required testid="project-title" placeholder="Ex : Rénovation électrique maison" />
        <SelectField label="Type de projet" value={f.project_type} onChange={set("project_type")} testid="project-type" placeholder={null} options={PROJECT_TYPES} />
        <TextArea label="Description du besoin" value={f.description} onChange={set("description")} required testid="project-description" rows={4} placeholder="Décrivez votre projet, surfaces, contraintes…" />
        <div className="grid grid-cols-2 gap-3">
          <TextField label="Budget estimé (FCFA)" value={f.budget_estimate} onChange={set("budget_estimate")} testid="project-budget" placeholder="Ex : 500 000" />
          <TextField label="Délai souhaité" value={f.deadline} onChange={set("deadline")} testid="project-deadline" placeholder="Ex : sous 2 mois" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <TextField label="Zone" value={f.zone} onChange={set("zone")} testid="project-zone" placeholder="Ex : Lomé Centre" />
          <TextField label="Adresse" value={f.address} onChange={set("address")} testid="project-address" placeholder="Quartier, repère…" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-600 mb-1">Photos / plans</label>
          <label className="flex items-center gap-2 border border-dashed border-slate-300 rounded-lg px-3 py-2.5 text-sm text-slate-500 cursor-pointer hover:border-[#C85A32]" data-testid="project-upload-label">
            <Upload className="w-4 h-4" /> {uploading ? "Envoi en cours…" : "Ajouter des fichiers"}
            <input type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={upload} data-testid="project-upload-input" />
          </label>
          {media.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {media.map((m, i) => (
                <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border border-slate-200 bg-slate-50" data-testid={`project-media-${i}`}>
                  <img src={m} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => setMedia((arr) => arr.filter((_, j) => j !== i))} className="absolute top-0.5 right-0.5 bg-white/90 rounded-full p-0.5"><X className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
        <button className="w-full bg-[#C85A32] text-white font-bold py-3 rounded-lg disabled:opacity-60" disabled={uploading} data-testid="project-submit">Soumettre mon projet</button>
      </form>
    </Modal>
  );
}

export function ProjectThread({ id, onClose, onChanged, staff }) {
  const [p, setP] = useState(null);
  const [msg, setMsg] = useState("");
  const load = useCallback(() => api.get(`/projects/${id}`).then((r) => setP(r.data)), [id]);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);

  const send = async () => {
    if (!msg.trim()) return;
    try { await api.post(`/projects/${id}/messages`, { message: msg }); setMsg(""); load(); onChanged && onChanged(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const stepIdx = p ? STEPS.indexOf(p.status) : -1;

  return (
    <Modal title={p ? `${p.project_number} — ${p.title}` : "Projet"} onClose={onClose} testid="project-thread" size="lg">
      {!p ? <Loader /> : (
        <div className="space-y-3">
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${PROJECT_STATUS[p.status]?.color}`} data-testid="project-thread-status">{PROJECT_STATUS[p.status]?.label}</span>

          {p.status !== "refuse" && (
            <div className="flex items-center gap-1 flex-wrap" data-testid="project-timeline">
              {STEPS.map((s, i) => (
                <div key={s} className="flex items-center gap-1">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${i <= stepIdx ? "bg-[#C85A32] text-white" : "bg-slate-100 text-slate-400"}`}>{PROJECT_STATUS[s].label}</span>
                  {i < STEPS.length - 1 && <span className="text-slate-300">›</span>}
                </div>
              ))}
            </div>
          )}

          <div className="bg-slate-50 rounded-lg p-3 space-y-1">
            <p className="text-sm whitespace-pre-line">{p.description}</p>
            <div className="text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-0.5 pt-1">
              {p.project_type && <span>Type : {PROJECT_TYPES.find((t) => t.value === p.project_type)?.label || p.project_type}</span>}
              {p.budget_estimate && <span>Budget : {p.budget_estimate} FCFA</span>}
              {p.deadline && <span>Délai : {p.deadline}</span>}
              {p.zone && <span>Zone : {p.zone}</span>}
              {p.address && <span>Adresse : {p.address}</span>}
            </div>
            {(p.media || []).length > 0 && (
              <div className="flex flex-wrap gap-2 pt-2">
                {p.media.map((m, i) => (
                  <a key={i} href={m} target="_blank" rel="noreferrer" className="w-14 h-14 rounded-lg overflow-hidden border border-slate-200 bg-white block">
                    <img src={m} alt="" className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {(p.messages || []).length === 0 && <p className="text-xs text-slate-400 text-center py-2">Aucun échange pour le moment.</p>}
            {(p.messages || []).map((m, i) => (
              <div key={i} className={`rounded-lg p-3 ${m.is_staff ? "bg-[#FDF3EE] ml-6" : "bg-slate-50 mr-6"}`} data-testid={`project-msg-${i}`}>
                <p className="text-xs text-slate-400 mb-1">{m.author}{m.is_staff ? " · TOGOBATI" : ""} · {formatDate(m.at)}</p>
                <p className="text-sm">{m.message}</p>
              </div>
            ))}
          </div>

          {["cloture", "refuse"].includes(p.status) && !staff ? (
            <p className="text-xs text-center text-slate-400">Ce projet est {PROJECT_STATUS[p.status].label.toLowerCase()}.</p>
          ) : (
            <div className="flex gap-2">
              <input value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Votre message…" className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="project-reply-input" />
              <button onClick={send} className="bg-[#C85A32] text-white px-4 rounded-lg inline-flex items-center gap-1" data-testid="project-reply-send"><Send className="w-4 h-4" /></button>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
